import { DerivativeSettlementTypeEnum, getOptionCoin, type SecurityTypes } from '@talos/kyoko';
import { differenceInHours, isSameDay } from 'date-fns';
import { ALL_EXPIRIES_ITEM, CME_MARKET_NAME, STS_DIGITAL_MARKET_NAME } from '../types';
import type { OptionFilteringPanelState, OptionFilteringState } from './types';

/**
 * Validate the symbol to make it a valid one, for example,
 * ensure that given the selected market the currency is allowed.
 * Or that the expiry is valid given the market and currency.
 */
export function validateSymbol(
  optionFilter: OptionFilteringPanelState['optionFilter'],
  meta: OptionFilteringState['meta']
): OptionFilteringPanelState['optionFilter'] {
  return resolveSettlement(resolveSymbolIdentity(optionFilter, meta), meta);
}

function resolveSymbolIdentity(
  optionFilter: OptionFilteringPanelState['optionFilter'],
  meta: OptionFilteringState['meta']
): OptionFilteringPanelState['optionFilter'] {
  // If the filter doesn't have underlyingCode defined (coin), fallback to currency
  const coin = optionFilter.underlyingCode || optionFilter.currency;
  const { expiry, market, currency, marketAccountName } = optionFilter;

  const { expirationByMarketByCurrencyIdentity, marketAccountsByMarket } = meta;
  const validatedCoin = validateCoinForMarketAndBaseCurrency(coin, market, currency, meta);

  const validExpirySet = expirationByMarketByCurrencyIdentity.get(validatedCoin)?.get(market) || new Set();
  const validMarketSet = new Set(expirationByMarketByCurrencyIdentity.get(validatedCoin)?.keys() || []);

  const validMarket = (validMarketSet.has(market) ? market : Array.from(validMarketSet).at(0)) || market;
  const validMarketAccountNameSet = new Set(marketAccountsByMarket.get(validMarket)?.map(m => m.Name) || []);

  const validMarketAccountName =
    marketAccountName !== null && validMarketAccountNameSet.has(marketAccountName)
      ? marketAccountName
      : (Array.from(validMarketAccountNameSet).at(0) ?? null);

  const allExpiriesAllowed = showAllExpiriesOptionForMarket(validMarket);
  const allExpiriesOrNull = allExpiriesAllowed ? ALL_EXPIRIES_ITEM : null;

  if (validExpirySet.size === 0) {
    // There are no valid expiries
    return {
      ...optionFilter,
      underlyingCode: validatedCoin,
      market: validMarket,
      marketAccountName: validMarketAccountName,
      expiry: allExpiriesOrNull,
    };
  }

  // The selected expiry is valid if we allow ALL and the selection is ALL, OR the selected expiry is in the Valid expiries set.
  const isExpiryValid = (allExpiriesAllowed && expiry === ALL_EXPIRIES_ITEM) || (expiry && validExpirySet.has(expiry));
  if (isExpiryValid) {
    return {
      ...optionFilter,
      underlyingCode: validatedCoin,
      market: validMarket,
      marketAccountName: validMarketAccountName,
      expiry,
    };
  }

  /**
   * Else, expiry is invalid. Need to default it in some way. There are three possible cases.
   * 1. expiry is null. We should set it to ALL if thats allowed, otherwise set it to the closest expiry to NOW as possible.
   * 2. expiry is ALL and that is not allowed. We set it to the closest expiry to NOW as possible.
   * 3. expiry is defined but the expiry doesnt exist anymore. We try to set it to any existing expiry on the same day. If none exists, set to ALL if thats allowed, otherwise closest.
   */

  let workingExpiry = expiry;

  if (expiry == null) {
    // 1
    workingExpiry = allExpiriesAllowed ? ALL_EXPIRIES_ITEM : getClosestExpiry(new Date(), [...validExpirySet.values()]);
  } else if (expiry === ALL_EXPIRIES_ITEM && !allExpiriesAllowed) {
    // 2
    workingExpiry = getClosestExpiry(new Date(), [...validExpirySet.values()]);
  } else {
    // 3
    const prevExpiryDate = new Date(expiry);
    const closestExpiryToPrevExpiry = getClosestExpiry(new Date(expiry), [...validExpirySet.values()]);

    const newExpiryIsSameDayAsPrevious =
      closestExpiryToPrevExpiry && isSameDay(new Date(closestExpiryToPrevExpiry), prevExpiryDate);

    if (newExpiryIsSameDayAsPrevious) {
      workingExpiry = closestExpiryToPrevExpiry;
    } else {
      // The closest expiry we found is not the same day as the previously selected one.
      // If we are allowed to select ALL, select it. If we cannot select ALL, select the closest one we found.
      workingExpiry = allExpiriesAllowed ? ALL_EXPIRIES_ITEM : closestExpiryToPrevExpiry;
    }
  }

  return {
    ...optionFilter,
    underlyingCode: validatedCoin,
    market: validMarket,
    marketAccountName: validMarketAccountName,
    expiry: workingExpiry,
  };
}

function validateCoinForMarketAndBaseCurrency(
  selectedCoin: string,
  market: string,
  currency: string,
  meta: OptionFilteringState['meta']
) {
  const availableCoins = getValidCoinsForMarketAndBaseCurrency(meta, market, currency);
  if (availableCoins.includes(selectedCoin)) {
    // If the current selection is in the set of availables, keep the selection
    return selectedCoin;
  }

  // Otherwise reset to the first available selection
  return availableCoins.at(0) ?? selectedCoin;
}

function getValidCoinsForMarketAndBaseCurrency(
  meta: OptionFilteringState['meta'],
  market: string,
  currency: string
): string[] {
  const coins = new Set<string>();
  for (const option of meta.optionSecurityBySymbol.values()) {
    if (option.BaseCurrency === currency && (option.Markets?.includes(market) ?? false)) {
      // If the base currency and market matches, this is a valid option. Add it to the set of availables.
      coins.add(getOptionCoin(option));
    }
  }

  // Sort so we get stable fallbacking behavior since we do .at(0) on this resulting set
  return Array.from(coins).toSorted();
}

/**
 * Returns whether or not the user is allowed to view all expiries in the Options Chain. Depends on the Market selected.
 */
export function showAllExpiriesOptionForMarket(market: string): boolean {
  if (isMarketCME(market)) {
    return false;
  }

  return true;
}

export function isMarketCME(market: string) {
  return market.toLowerCase() === CME_MARKET_NAME;
}

/**
 * Given some reference date and an array of expiries (as date strings), returns the closest expiry date string to the reference date.
 *
 * The difference computation on the dates is done at an hourly increment.
 */
function getClosestExpiry(referenceDate: Date, expiries: string[]): string | null {
  const expiriesSortedByTimeDiff = expiries.toSorted((a, b) => {
    const diffA = Math.abs(differenceInHours(referenceDate, new Date(a)));
    const diffB = Math.abs(differenceInHours(referenceDate, new Date(b)));
    return diffA - diffB;
  });

  return expiriesSortedByTimeDiff.at(0) ?? null;
}

/**
 * Whether the given market exposes per-instrument settlement variants (Settlement Type / Currency).
 * Currently only STS Digital does.
 */
export function marketHasSettlementOptions(market: string | undefined): boolean {
  return market === STS_DIGITAL_MARKET_NAME;
}

/** Settlement-bearing options for a base currency on a settlement market. */
function getSettlementOptionsForCoin(
  meta: OptionFilteringState['meta'],
  currency: string,
  underlyingCode: string | undefined,
  market: string
): SecurityTypes['Option'][] {
  const result: SecurityTypes['Option'][] = [];
  for (const option of meta.optionSecurityBySymbol.values()) {
    if (
      option.DerivativeSettlementType != null &&
      option.BaseCurrency === currency &&
      (!underlyingCode || getOptionCoin(option) === underlyingCode) &&
      (option.Markets?.includes(market) ?? false)
    ) {
      result.push(option);
    }
  }
  return result;
}

/** Distinct settlement types (Cash / Physical) available for the selected coin on a settlement market. */
export function getSettlementTypeOptions(
  meta: OptionFilteringState['meta'],
  currency: string,
  underlyingCode: string | undefined,
  market: string
): DerivativeSettlementTypeEnum[] {
  const types = new Set<DerivativeSettlementTypeEnum>();
  for (const option of getSettlementOptionsForCoin(meta, currency, underlyingCode, market)) {
    if (option.DerivativeSettlementType) {
      types.add(option.DerivativeSettlementType);
    }
  }
  return Array.from(types);
}

/**
 * Distinct settlement currency codes available for the selected coin and settlement type.
 * For Cash this is the base and quote coins; for Physical it is the (base) settlement coin.
 */
export function getSettlementCurrencyOptions(
  meta: OptionFilteringState['meta'],
  currency: string,
  underlyingCode: string | undefined,
  market: string,
  settlementType: DerivativeSettlementTypeEnum | undefined
): string[] {
  const currencies = new Set<string>();
  for (const option of getSettlementOptionsForCoin(meta, currency, underlyingCode, market)) {
    if (settlementType && option.DerivativeSettlementType !== settlementType) {
      continue;
    }
    if (option.SettlementCurrency) {
      currencies.add(option.SettlementCurrency);
    }
  }
  return Array.from(currencies);
}

/**
 * Default the settlement type/currency for the selected coin. Cash defaults to the quote currency;
 * Physical settles in (and locks to) the base currency. Non-settlement markets clear both fields.
 * An existing, still-valid selection is preserved.
 */
export function resolveSettlement(
  optionFilter: OptionFilteringPanelState['optionFilter'],
  meta: OptionFilteringState['meta']
): OptionFilteringPanelState['optionFilter'] {
  if (!marketHasSettlementOptions(optionFilter.market)) {
    if (optionFilter.settlementType === undefined && optionFilter.settlementCurrency === undefined) {
      return optionFilter;
    }
    return { ...optionFilter, settlementType: undefined, settlementCurrency: undefined };
  }

  const settlementOptions = getSettlementOptionsForCoin(
    meta,
    optionFilter.currency,
    optionFilter.underlyingCode,
    optionFilter.market
  );
  if (settlementOptions.length === 0) {
    return { ...optionFilter, settlementType: undefined, settlementCurrency: undefined };
  }

  const availableTypes = getSettlementTypeOptions(
    meta,
    optionFilter.currency,
    optionFilter.underlyingCode,
    optionFilter.market
  );
  const settlementType =
    optionFilter.settlementType && availableTypes.includes(optionFilter.settlementType)
      ? optionFilter.settlementType
      : availableTypes.includes(DerivativeSettlementTypeEnum.Cash)
        ? DerivativeSettlementTypeEnum.Cash
        : availableTypes[0];

  let settlementCurrency: string | undefined;
  if (settlementType === DerivativeSettlementTypeEnum.Physical) {
    // Physical settles in the base currency.
    settlementCurrency = optionFilter.currency;
  } else {
    const availableCurrencies = getSettlementCurrencyOptions(
      meta,
      optionFilter.currency,
      optionFilter.underlyingCode,
      optionFilter.market,
      settlementType
    );
    // The quote-settled variant settles in the contract's quote currency.
    const quoteCurrency = settlementOptions.find(o => o.SettlementCurrency === o.QuoteCurrency)?.SettlementCurrency;
    settlementCurrency =
      optionFilter.settlementCurrency && availableCurrencies.includes(optionFilter.settlementCurrency)
        ? optionFilter.settlementCurrency
        : quoteCurrency && availableCurrencies.includes(quoteCurrency)
          ? quoteCurrency
          : availableCurrencies[0];
  }

  return { ...optionFilter, settlementType, settlementCurrency };
}
